/**
 * Common Types for MEV-Med System
 */

import { ReactNode } from 'react';

export interface ApiResponse<T = unknown> {
  message: string;
  data?: T;
  error?: string;
  code?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

/**
 * A recusa vinda do servidor, no formato que o httpClient rejeita.
 *
 * Precisa ser uma instância de `Error` porque as telas escolhem entre a mensagem do servidor e
 * um texto genérico com `err instanceof Error`. Um objeto literal reprova nesse teste e faz o
 * motivo real da recusa ser descartado — o usuário lê "Erro ao salvar" e não sabe o que corrigir.
 */
export class ApiRequestError extends Error implements ApiError {
  readonly code?: string;
  readonly status?: number;
  readonly details?: Record<string, unknown>;

  constructor({ message, code, status, details }: ApiError) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as ApiError).message === 'string'
  );
}

/**
 * Extract error message from a caught error (ApiError or unknown)
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    return error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

export interface FormValidationError {
  field: string;
  message: string;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: {
    label: string;
    handler: () => void;
  };
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

export interface TableColumn<T = Record<string, unknown>> {
  id: keyof T;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: unknown) => string | ReactNode;
  sortable?: boolean;
}

// Brazilian states - centralized to avoid conflicts
export const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
] as const;

export type BrazilianState = typeof BRAZILIAN_STATES[number];